import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import {CheckCircleOutline, Close, Search, SupportAgent, Visibility} from "@mui/icons-material";
import {LoadingButton} from "@mui/lab";
import React, {useCallback, useEffect, useRef, useState} from "react";
import dayjs from "dayjs";
import {toast} from "react-toastify";
import {When} from "react-if";
import Loading from "../../../../../components/loading";
import NoData from "../../../../../components/no-data";
import palette from "../../../../../theme/palette";
import {calculateAge} from "../../../../../helpers";
import {capitalizeFirstLetter, getRiskFactorChipStyle, getRiskFactorTexts} from "../../../../../helpers/crimeScore";
import {IraSearchHistoryService} from "../../../../../services/ira-search-history.service";
import {IraSearchHistoryTypes} from "../../../../../types";
import {RESPONSE_CODES} from "../../../../../constants/responseCodes.constants";

// 'flagged' is the URL value (?view=flagged); the UI calls it "Reported to POM Support".
export type AllSearchesView = 'all' | 'flagged';

// Mirrors pom-2-api's flagReference(): "IRA-" + 8 hex digits.
const REFERENCE_PATTERN = /^IRA-[0-9A-F]{8}$/;
export const normalizeReference = (value: string) => value.trim().toUpperCase();

interface Props {
    initialView: AllSearchesView;
    onViewChange?: (view: AllSearchesView) => void;
    /** A report reference to open straight away (the support email links with ?ref=). */
    initialReference?: string | null;
    onReferenceChange?: (reference: string | null) => void;
    /** POM support (masquerading or POM master): may resolve reports. */
    isSupport: boolean;
    /** Reporting is off while masquerading: it would go out as the customer's user. */
    canFlag: boolean;
    onOpenSearch: (searchId: string) => void;
    onFlag: (target: IraSearchHistoryTypes.FlagTarget) => void;
    /** Bumped by the parent after a report is sent, to refetch. */
    refreshKey: number;
}

const HEADER_SX = { color: 'text.secondary', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', whiteSpace: 'nowrap' } as const;

const SOURCE_LABELS: Record<string, string> = {
    safety_hub: 'Safety Hub',
    mobile: 'Mobile app',
    // Safety Hub searches were recorded as 'api' until 868mkwmk5, so an old
    // 'api' row may be either.
    api: 'Safety Hub or API',
};

const personName = (person: IraSearchHistoryTypes.SearchedPerson | null) =>
    [person?.firstName, person?.lastName].filter(Boolean).map((p) => capitalizeFirstLetter(p!)).join(' ');

const PersonCell: React.FC<{ person: IraSearchHistoryTypes.SearchedPerson | null; stored: boolean; unreadable?: boolean }> = ({ person, stored, unreadable }) => {
    const name = personName(person);
    if (!stored) {
        return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Result not stored
            </Typography>
        );
    }
    if (unreadable) {
        return (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                Details could not be decrypted
            </Typography>
        );
    }
    if (!name) {
        return (
            <Box>
                <Typography variant="body2" fontWeight={500} color="text.secondary" fontStyle="italic">
                    Address-only search
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    No name or date of birth searched
                </Typography>
            </Box>
        );
    }
    const dob = person?.dateOfBirth;
    // '--' only when there is no usable DOB: an infant's age is a real 0.
    const age = dob ? calculateAge(dob) : NaN;
    return (
        <Box>
            <Typography variant="body2" fontWeight={500}>{name}</Typography>
            <Typography variant="caption" color="text.secondary">
                {`Age ${Number.isFinite(age) ? age : '--'} • DOB: ${dob || '--'}`}
            </Typography>
        </Box>
    );
};

const DateCell: React.FC<{ value: string | null }> = ({ value }) => (
    <>
        <Typography variant="body2" color="text.secondary">{value ? dayjs(value).format('MMMM D, YYYY') : 'N/A'}</Typography>
        <Typography variant="body2" color="text.secondary">{value ? dayjs(value).format('h:mm A') : ''}</Typography>
    </>
);

const ReportStatusChip: React.FC<{ status: IraSearchHistoryTypes.FlagStatus }> = ({ status }) => (
    <Chip
        size="small"
        icon={status === 'open' ? <SupportAgent sx={{ fontSize: 14 }} /> : <CheckCircleOutline sx={{ fontSize: 14 }} />}
        label={status === 'open' ? 'With POM Support' : 'Resolved by POM Support'}
        sx={status === 'open'
            ? { bgcolor: '#FFF4E5', color: '#B76E00', fontSize: 11, height: 22 }
            : { bgcolor: '#EEF5EE', color: '#2E7D32', fontSize: 11, height: 22 }}
    />
);

// Safety Hub IRA "All Searches" (ClickUp 868mkwmk5): every search, saved or
// not, plus a "Reported to POM Support" view of results users sent to POM
// Support. Admins see the whole organization, other users their own; the API
// decides which. Each report has a reference (IRA-XXXXXXXX) that also appears
// in the support email, which links here with ?ref=.
export const AllSearches: React.FC<Props> = ({
    initialView, onViewChange, initialReference, onReferenceChange, isSupport, canFlag, onOpenSearch, onFlag, refreshKey,
}) => {
    const [view, setView] = useState<AllSearchesView>(initialReference ? 'flagged' : initialView);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [searches, setSearches] = useState<IraSearchHistoryTypes.Page<IraSearchHistoryTypes.SearchRow> | null>(null);
    const [flags, setFlags] = useState<IraSearchHistoryTypes.Page<IraSearchHistoryTypes.FlagRow> | null>(null);
    const [resolvingId, setResolvingId] = useState<string | null>(null);
    const validInitialReference = initialReference && REFERENCE_PATTERN.test(normalizeReference(initialReference))
        ? normalizeReference(initialReference)
        : null;
    const [reference, setReference] = useState<string | null>(validInitialReference);
    const [referenceInput, setReferenceInput] = useState<string>(validInitialReference ?? '');
    const [referenceError, setReferenceError] = useState<boolean>(false);

    // The table shows the spinner until the CURRENT query has answered, so it
    // never flashes the empty state before the first load or the other view's
    // rows while switching. `loaded` records which query the data (or error)
    // belongs to; loading is derived from it rather than toggled.
    const queryKey = [view, page, perPage, reference ?? '', refreshKey].join('|');
    const [loaded, setLoaded] = useState<{ key: string; error: string | null } | null>(null);
    const loading = loaded?.key !== queryKey;
    const loadError = loading ? null : loaded?.error ?? null;

    // Only the latest request may update the table: paging, the reference
    // filter and post-report refreshes can overlap, and a slower earlier
    // response must not overwrite newer data.
    const requestSeq = useRef(0);
    const load = useCallback(async () => {
        const seq = ++requestSeq.current;
        const isLatest = () => seq === requestSeq.current;
        try {
            if (view === 'all') {
                const result = await IraSearchHistoryService.listSearches(page + 1, perPage);
                if (isLatest()) setSearches(result);
            } else {
                const result = await IraSearchHistoryService.listFlags(page + 1, perPage, undefined, reference ?? undefined);
                if (isLatest()) setFlags(result);
            }
            if (isLatest()) setLoaded({ key: queryKey, error: null });
        } catch (e: any) {
            if (isLatest()) {
                console.error('[AllSearches] load failed:', e);
                const code: string | undefined = e?.response?.data?.message;
                setLoaded({ key: queryKey, error: (code && RESPONSE_CODES[code]) || 'Something went wrong. Please try again.' });
            }
        }
    }, [view, page, perPage, reference, queryKey]);

    useEffect(() => {
        load();
    }, [load]);

    // Clearing `loaded` brings the spinner back for the retry.
    const retry = () => {
        setLoaded(null);
        load();
    };

    const changeView = (next: AllSearchesView | null) => {
        if (!next || next === view) return;
        setView(next);
        setPage(0);
        // Leaving the reported view drops the reference filter; the parent drops
        // ?ref from the URL in the same step, so the two can't drift.
        if (next === 'all') {
            setReference(null);
            setReferenceInput('');
            setReferenceError(false);
        }
        onViewChange?.(next);
    };

    const applyReference = (value: string | null) => {
        setReference(value);
        setReferenceInput(value ?? '');
        setReferenceError(false);
        setPage(0);
        onReferenceChange?.(value);
    };

    const findReference = () => {
        const value = normalizeReference(referenceInput);
        if (!value) {
            applyReference(null);
            return;
        }
        if (!REFERENCE_PATTERN.test(value)) {
            setReferenceError(true);
            return;
        }
        applyReference(value);
    };

    const resolve = async (flag: IraSearchHistoryTypes.FlagRow) => {
        setResolvingId(flag.id);
        try {
            await IraSearchHistoryService.resolveFlag(flag.id);
            toast(`${flag.reference} resolved`, { type: 'success', autoClose: 1500 });
            await load();
        } catch (e) {
            console.error('[AllSearches] resolve failed:', e);
            toast('Could not resolve the report', { type: 'error', autoClose: 2000 });
        } finally {
            setResolvingId(null);
        }
    };

    const total = (view === 'all' ? searches?.total : flags?.total) ?? 0;
    const isEmpty = !loading && !loadError && (view === 'all' ? !searches?.items?.length : !flags?.items?.length);

    return (
        <Card
            sx={{
                maxWidth: 1240,
                mx: 'auto',
                borderRadius: '24px',
                border: '1px solid #E6EBF2',
                boxShadow: '0 12px 32px -20px rgba(14,18,48,0.16)',
                '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: '#F7F9FC',
                    borderBottom: '1px solid #E8EDF4',
                },
            }}
        >
            <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em', color: '#0E1230' }}>
                            {view === 'all' ? 'All Searches' : 'Reported to POM Support'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {view === 'all'
                                ? 'Every assessment that was run, saved or not'
                                : 'Results sent to the POM Support team for review. The reference matches the one in the support email.'}
                        </Typography>
                    </Box>
                    <ToggleButtonGroup size="small" exclusive value={view} onChange={(_, next) => changeView(next)}>
                        <ToggleButton value="all">All searches</ToggleButton>
                        <ToggleButton value="flagged">Reported to POM Support</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <When condition={view === 'flagged'}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            placeholder="Find reference, e.g. IRA-3F9A12C7"
                            value={referenceInput}
                            error={referenceError}
                            helperText={referenceError ? 'References look like IRA-3F9A12C7' : undefined}
                            onChange={(e) => { setReferenceInput(e.target.value); setReferenceError(false); }}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.repeat) findReference(); }}
                            inputProps={{ maxLength: 20, 'aria-label': 'Find reference' }}
                            sx={{ width: 300 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" aria-label="Find" onClick={findReference}><Search fontSize="small" /></IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {reference && (
                            <Chip
                                label={`Showing ${reference}`}
                                onDelete={() => applyReference(null)}
                                deleteIcon={<Close />}
                                size="small"
                            />
                        )}
                    </Box>
                </When>

                <TableContainer>
                    <When condition={view === 'all'}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['Name', 'Address', 'Risk Factors', 'Run By', 'Date', 'Status', 'Actions'].map((h) => (
                                        <TableCell key={h}><Typography variant="caption" sx={HEADER_SX}>{h}</Typography></TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <When condition={!loading && !loadError}>
                                <TableBody>
                                    {searches?.items?.map((row) => {
                                        const factors = row.riskAssessmentResult ? getRiskFactorTexts(row.riskAssessmentResult) : [];
                                        const runBy = row.runBy ?? (row.source === 'api' || !row.source ? 'Not recorded' : 'Removed user');
                                        return (
                                            <TableRow key={row.id} hover>
                                                <TableCell><PersonCell person={row.searchedPerson} stored={!!row.searchId} unreadable={row.unreadable} /></TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" color="text.secondary">{row.address ?? 'N/A'}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {factors?.map((factor, index) => (
                                                            <Chip key={index} label={factor} size="small" sx={{ fontSize: 10, height: 20, ...getRiskFactorChipStyle(factor) }} />
                                                        ))}
                                                        {!factors?.length && <Typography variant="body2" color="text.secondary">N/A</Typography>}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontWeight={500} color={row.runBy ? 'text.primary' : 'text.secondary'}>
                                                        {runBy}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.source ? SOURCE_LABELS[row.source] : ''}
                                                        {row.cached ? ' • repeat, showed the earlier result' : ''}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell><DateCell value={row.createdAt} /></TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                        {row.saved && <Chip size="small" label="Saved" variant="outlined" sx={{ fontSize: 11, height: 22 }} />}
                                                        {row.flagStatus && <ReportStatusChip status={row.flagStatus} />}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'nowrap' }}>
                                                        <Button
                                                            size="small"
                                                            variant="alternate"
                                                            startIcon={<Visibility />}
                                                            disabled={!row.searchId}
                                                            onClick={() => row.searchId && onOpenSearch(row.searchId)}
                                                        >
                                                            View
                                                        </Button>
                                                        {canFlag && (
                                                            // Icon-only in the table so the row stays one line; the
                                                            // tooltip and the dialog say it goes to POM Support.
                                                            <Tooltip title={row.unreadable
                                                                ? "This result's details can't be decrypted, so it can't be reported here. Email support@pomsafe.com with the date and time of the search instead."
                                                                : 'Report to POM Support. It goes to the POM Support team, not to anyone in your organization.'}>
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        aria-label="Report to POM Support"
                                                                        disabled={!row.searchId || !!row.unreadable}
                                                                        onClick={() => row.searchId && !row.unreadable && onFlag({ searchId: row.searchId })}
                                                                        sx={{
                                                                            border: '1px solid',
                                                                            borderColor: 'divider',
                                                                            borderRadius: '8px',
                                                                            color: palette.darkBlue,
                                                                        }}
                                                                    >
                                                                        <SupportAgent fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </When>
                        </Table>
                    </When>

                    <When condition={view === 'flagged'}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {['Reference', 'Reported', 'Reported By', 'Search', 'Comment', 'Status', 'Actions'].map((h) => (
                                        <TableCell key={h}><Typography variant="caption" sx={HEADER_SX}>{h}</Typography></TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <When condition={!loading && !loadError}>
                                <TableBody>
                                    {flags?.items?.map((flag) => (
                                        <TableRow key={flag.id} hover selected={flag.reference === reference}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                    {flag.reference}
                                                </Typography>
                                            </TableCell>
                                            <TableCell><DateCell value={flag.createdAt} /></TableCell>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500}>{flag.createdBy ?? 'Removed user'}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ minWidth: 200 }}>
                                                <PersonCell person={flag.searchedPerson} stored={!!(flag.searchId || flag.reportId)} unreadable={flag.unreadable} />
                                                <Typography variant="caption" color="text.secondary" display="block">{flag.address ?? ''}</Typography>
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 360 }}>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{flag.comment}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <ReportStatusChip status={flag.status} />
                                                {flag.resolvedAt && (
                                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                                        {dayjs(flag.resolvedAt).format('MMM D, YYYY')}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <Tooltip title={flag.searchId ? '' : 'Open this one from Saved reports'}>
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="alternate"
                                                                startIcon={<Visibility />}
                                                                disabled={!flag.searchId}
                                                                onClick={() => flag.searchId && onOpenSearch(flag.searchId)}
                                                            >
                                                                View
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    {isSupport && flag.status === 'open' && (
                                                        <LoadingButton
                                                            size="small"
                                                            variant="outlined-alternate"
                                                            startIcon={<CheckCircleOutline />}
                                                            loading={resolvingId === flag.id}
                                                            onClick={() => resolve(flag)}
                                                        >
                                                            Resolve
                                                        </LoadingButton>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </When>
                        </Table>
                    </When>

                    <When condition={loading}>
                        <Loading color={palette.darkBlue}/>
                    </When>
                    <When condition={!!loadError}>
                        <NoData
                            buttonText="Try again"
                            onClick={retry}
                            title={view === 'all' ? 'Could not load searches' : 'Could not load reports'}
                            description={loadError ?? ''}
                        />
                    </When>
                    <When condition={isEmpty}>
                        <NoData
                            buttonText={null}
                            onClick={() => {}}
                            title={view === 'all'
                                ? 'No searches found'
                                : reference ? `No report ${reference} in this organization` : 'Nothing reported to POM Support'}
                            description={view === 'all'
                                ? 'Intake risk assessments will show here once they are run.'
                                : reference
                                    ? 'Coming from a support email? Masquerade into the organization it names, then open the link again.'
                                    : 'Results reported to POM Support will show here with their reference.'}
                        />
                    </When>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={perPage}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setPage(0); setPerPage(parseInt(e.target.value, 10)); }}
                />
            </CardContent>
        </Card>
    );
};
